import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getSubscriptionPaymentLink,
  listMyWebSubscriptions,
  USE_API_MEAL_PLANS,
} from '../../api/mealPlansApi';
import SubscriptionReceiptModal from '../../components/SubscriptionReceipt/SubscriptionReceiptModal';
import { buildInvoiceFromWebSubscription } from '../../components/SubscriptionReceipt/subscriptionReceiptUtils';
import { clearStoredAuthIfApiError, readStoredAuth } from './profileFormUtils';
import AccountSessionError from './AccountSessionError';
import AccountSubscriptionDetailsModal from './AccountSubscriptionDetailsModal';
import AccountSubscriptionDeliveriesPanel from './AccountSubscriptionDeliveriesPanel';
import { formatDisplayDate } from '../../utils/dateFormat';
import {
  formatSpecialtyWithRegion,
  formatSubscriptionMealSlots,
  resolvePlanLabel,
} from '../../utils/subscriptionDisplay';
import {
  isApprovedPaidSubscription,
  isPausedSubscription,
} from './subscriptionActionUtils';
import './AccountLayout.css';

const SUB_TABS = [
  { id: 'all', label: 'All' },
  { id: 'pending', label: 'Pending' },
  { id: 'active', label: 'Active' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'past', label: 'Past' },
];

function formatMoney(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toFixed(2)}`;
}

function formatDate(value) {
  return formatDisplayDate(value, '—');
}

function statusBucket(orderStatus) {
  const s = String(orderStatus || '').toUpperCase();
  if (s === 'PENDING' || s === 'PAYMENTPENDING') return 'pending';
  if (s === 'CANCELLED') return 'cancelled';
  if (s === 'COMPLETED') return 'past';
  if (s === 'APPROVED' || s === 'PAUSED' || s === 'CLOSEDKITCHEN') return 'active';
  return 'other';
}

function statusLabel(orderStatus) {
  switch (String(orderStatus || '').toUpperCase()) {
    case 'PENDING':
      return 'Pending approval';
    case 'PAYMENTPENDING':
      return 'Payment pending';
    case 'APPROVED':
      return 'Active';
    case 'PAUSED':
      return 'Paused';
    case 'CLOSEDKITCHEN':
      return 'Kitchen closed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'COMPLETED':
      return 'Completed';
    default:
      return orderStatus || '—';
  }
}

function statusTone(orderStatus) {
  const bucket = statusBucket(orderStatus);
  if (bucket === 'pending') return 'pending';
  if (bucket === 'cancelled') return 'cancelled';
  if (String(orderStatus).toUpperCase() === 'COMPLETED') return 'completed';
  if (String(orderStatus).toUpperCase() === 'PAUSED' || String(orderStatus).toUpperCase() === 'CLOSEDKITCHEN') {
    return 'paused';
  }
  return 'active';
}

function paymentSummary(sub) {
  const status = String(sub.orderStatus || '').toUpperCase();
  const paidAmt = Number(sub.amountPaid);
  const isUnpaidStatus = status === 'PENDING' || status === 'PAYMENTPENDING';
  const actuallyPaid =
    sub.isPaid === true
    || (!isUnpaidStatus && Number.isFinite(paidAmt) && paidAmt > 0);

  if (actuallyPaid) {
    return { label: 'Paid', value: formatMoney(paidAmt) };
  }
  if (status === 'PAYMENTPENDING') {
    return {
      label: 'Amount due',
      value: formatMoney(sub.remainingAmount ?? sub.totalPrice),
    };
  }
  if (isUnpaidStatus || status === 'CANCELLED') {
    return { label: 'Paid', value: formatMoney(0) };
  }
  return { label: 'Paid', value: formatMoney(Number.isFinite(paidAmt) ? paidAmt : 0) };
}

function slotLabels(sub) {
  return formatSubscriptionMealSlots(sub, sub.webPlanSnapshot || {});
}

export default function AccountSubscriptions() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionError, setSessionError] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [detailsSub, setDetailsSub] = useState(null);
  const [receiptSub, setReceiptSub] = useState(null);
  const [holdsSub, setHoldsSub] = useState(null);

  const receiptInvoice = useMemo(
    () => (receiptSub ? buildInvoiceFromWebSubscription(receiptSub) : null),
    [receiptSub],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setSessionError(false);
    try {
      if (!USE_API_MEAL_PLANS) {
        setError('API meal plans are disabled.');
        setRows([]);
        return;
      }
      const { token } = readStoredAuth();
      if (!token) {
        setSessionError(true);
        return;
      }
      const data = await listMyWebSubscriptions();
      setRows(data?.Subscriptions || []);
    } catch (err) {
      if (clearStoredAuthIfApiError(err) || err?.status === 401) {
        setSessionError(true);
      } else {
        setError(err?.message || 'Unable to load subscriptions.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const next = { all: rows.length, pending: 0, active: 0, cancelled: 0, past: 0 };
    rows.forEach((sub) => {
      const bucket = statusBucket(sub.orderStatus);
      if (bucket === 'pending') next.pending += 1;
      if (bucket === 'active') next.active += 1;
      if (bucket === 'cancelled') next.cancelled += 1;
      if (bucket === 'past') next.past += 1;
    });
    return next;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (tab === 'all') return rows;
    return rows.filter((sub) => statusBucket(sub.orderStatus) === tab);
  }, [rows, tab]);

  const onPay = async (sub) => {
    setPayingId(sub.id);
    setError('');
    try {
      let url = sub.paymentLinkUrl;
      if (!url) {
        const res = await getSubscriptionPaymentLink(sub.id);
        url = res?.shortUrl;
      }
      if (!url) throw new Error('Payment link not available yet. Wait for kitchen approval.');
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err?.message || 'Unable to open payment link.');
    } finally {
      setPayingId(null);
    }
  };

  if (sessionError) {
    return <AccountSessionError title="Subscriptions" />;
  }

  if (holdsSub) {
    return (
      <AccountSubscriptionDeliveriesPanel
        subscription={rows.find((row) => row.id === holdsSub.id) || holdsSub}
        onBack={() => setHoldsSub(null)}
        onUpdated={load}
      />
    );
  }

  return (
    <div className="account-panel">
      <header className="account-panel__header">
        <h1>Subscriptions</h1>
        <p>Track your meal plan subscriptions, payments, and deliveries.</p>
      </header>

      <div className="account-sub-tabs" role="tablist" aria-label="Subscription filters">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`account-sub-tab${tab === t.id ? ' account-sub-tab--selected' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <span>{t.label}</span>
            <span className="account-sub-tab__badge">{counts[t.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading && <p>Loading…</p>}
      {error && <p className="account-form__error" role="alert">{error}</p>}

      {!loading && !error && rows.length === 0 && (
        <p>
          No meal plan subscriptions yet.{' '}
          <a href="/meal-plans">Create a meal plan</a>.
        </p>
      )}

      {!loading && rows.length > 0 && filteredRows.length === 0 && (
        <p className="account-sub-empty-tab">No {tab} subscriptions.</p>
      )}

      <ul className="account-sub-list">
        {filteredRows.map((sub) => {
          const snap = sub.webPlanSnapshot || {};
          const canPay = sub.orderStatus === 'PAYMENTPENDING';
          const canManageDeliveries = isApprovedPaidSubscription(sub) || isPausedSubscription(sub);
          const pay = paymentSummary(sub);
          const specialty = formatSpecialtyWithRegion(sub);
          const planName = resolvePlanLabel(sub);
          const diet = snap.isOrganic != null ? (snap.isOrganic ? 'Organic' : 'Regular') : null;
          const left = sub.remainingDeliveries ?? sub.count;
          const totalDays = sub.originalCount;
          return (
            <li key={sub.id} className="account-sub-card account-sub-card--list">
              <div className="account-sub-card__head">
                <div className="account-sub-card__identity">
                  <div className="account-sub-card__title-row">
                    <strong className="account-sub-card__number">{sub.subscriptionNumber}</strong>
                    <span className={`account-sub-status account-sub-status--${statusTone(sub.orderStatus)}`}>
                      {statusLabel(sub.orderStatus)}
                    </span>
                  </div>
                  <p className="account-sub-card__plan">
                    <span>{specialty}</span>
                    {diet ? <span className="account-sub-card__dot">{diet}</span> : null}
                  </p>
                  {planName && planName !== '—' ? (
                    <p className="account-sub-card__plan-name">
                      <span className="account-sub-card__slots-label">Plan</span>
                      {planName}
                    </p>
                  ) : null}
                  <p className="account-sub-card__slots">
                    <span className="account-sub-card__slots-label">Meal slots</span>
                    {slotLabels(sub)}
                  </p>
                </div>
                <div className="account-sub-card__actions">
                  {canPay ? (
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={payingId === sub.id}
                      onClick={() => onPay(sub)}
                    >
                      {payingId === sub.id ? 'Opening…' : 'Pay now'}
                    </button>
                  ) : null}
                  {canManageDeliveries ? (
                    <button
                      type="button"
                      className="account-sub-card__details-btn"
                      onClick={() => setHoldsSub(sub)}
                    >
                      Manage deliveries
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="account-sub-card__details-btn"
                    onClick={() => setReceiptSub(sub)}
                  >
                    View receipt
                  </button>
                  <button
                    type="button"
                    className="account-sub-card__details-btn"
                    onClick={() => setDetailsSub(sub)}
                  >
                    View details
                  </button>
                </div>
              </div>

              <div className="account-sub-card__stats" aria-label="Subscription summary">
                <div>
                  <span>Start</span>
                  <strong>{formatDate(sub.fromDate)}</strong>
                </div>
                <div>
                  <span>End</span>
                  <strong>{formatDate(sub.toDate)}</strong>
                </div>
                <div>
                  <span>Total</span>
                  <strong>{formatMoney(sub.totalPrice)}</strong>
                </div>
                <div>
                  <span>{pay.label}</span>
                  <strong>{pay.value}</strong>
                </div>
                <div>
                  <span>Deliveries left</span>
                  <strong>
                    {left != null ? left : '—'}
                    {totalDays != null ? ` / ${totalDays}` : ''}
                  </strong>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {detailsSub ? (
        <AccountSubscriptionDetailsModal
          subscription={detailsSub}
          onClose={() => setDetailsSub(null)}
          onPay={onPay}
          paying={payingId === detailsSub.id}
          onManageDeliveries={
            (isApprovedPaidSubscription(detailsSub) || isPausedSubscription(detailsSub))
              ? (sub) => {
                  setDetailsSub(null);
                  setHoldsSub(sub);
                }
              : undefined
          }
        />
      ) : null}

      {receiptInvoice ? (
        <SubscriptionReceiptModal
          invoiceData={receiptInvoice}
          onClose={() => setReceiptSub(null)}
          title="Subscription Receipt"
        />
      ) : null}

    </div>
  );
}
